import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Settings } from '../settings';
import './tooltip.scss'
import { useShowOnHover } from './use-show-on-hover';

export interface TooltipProps {
    content: React.ReactNode;
    placement: 'top' | 'bottom' | 'left' | 'right';
    padding?: number;
    trigger: { current: HTMLElement | null };
    boundary: { current: HTMLElement | null };
    boundaryMargin?: number;
    arrowBuffer?: number;
    visible?: boolean;
    update?: Date;
}

function useForceUpdate() {
    const [, setValue] = useState(0);
    const update = useCallback(() => setValue(prev => prev + 1), []);
    return update;
}

type EventType = keyof (WindowEventHandlersEventMap & GlobalEventHandlersEventMap)
function useUpdateOnWindowEvent(event: EventType) {
    const forceUpdate = useForceUpdate();

    useEffect(() => {
        window.addEventListener(event, forceUpdate)
        return () => window.removeEventListener(event, forceUpdate);
    }, [forceUpdate])
}

export function Tooltip(props: TooltipProps) {
    useUpdateOnWindowEvent('scroll');
    useUpdateOnWindowEvent('resize');

    const padding = typeof props.padding === 'undefined' ? 8 : props.padding;
    const boundaryMargin = typeof props.boundaryMargin === 'undefined' ? 8 : props.boundaryMargin;
    const { hoverEvents, ...childAttributes } = useShowOnHover(props.visible);

    const { trigger, boundary, placement: propsPlacement } = props;
    const triggerBoundingBox = trigger.current?.getBoundingClientRect();
    const boundaryBox = boundary.current?.getBoundingClientRect();

    const tooltipRefCallback = ((element: HTMLDivElement | null) => {
        if (!element) { return; }
        const tooltipBoundingBox = element.getBoundingClientRect();

        const arrow = element.querySelector('.lcms-arrow') as HTMLDivElement | undefined;
        if (!arrow || !triggerBoundingBox || !boundaryBox) {
            element.style.display = 'none';
            return;
        } else {
            element.style.display = '';
        }

        if (!tooltipBoundingBox.width || !tooltipBoundingBox.height) {
            element.style.left = '-99999';
            setTimeout(() => { tooltipRefCallback(element) });
            return;
        }


        const capX = (x: number, width: number) => Math.min((boundaryBox.left + boundaryBox.width - boundaryMargin) - width , Math.max(boundaryBox.left + boundaryMargin, x));
        const capY = (y: number, height: number) => Math.min((boundaryBox.top + boundaryBox.height - boundaryMargin) - height, Math.max(boundaryBox.top + boundaryMargin, y));
        const cap = (pos: { left: number, top: number }, dimensions: { width: number, height: number }) => {
            return {
                left: capX(pos.left, dimensions.width),
                top: capY(pos.top, dimensions.height),
            }
        }

        const wouldCap = (pos: { left: number, top: number }, dimensions: { width: number, height: number }) => {
            const capValues = cap(pos, dimensions);
            return {
                y: capValues.top > pos.top ? -1 : capValues.top < pos.top ? 1 : 0,
                x: capValues.left > pos.left ? -1 : capValues.left < pos.left ? 1 : 0
            };
        }

        let placement = propsPlacement;

        const defaultPosition = ((test: string) => (test === 'bottom' ? {
            left: ((triggerBoundingBox.left + triggerBoundingBox.width / 2) - tooltipBoundingBox.width / 2),
            top: (triggerBoundingBox.bottom + padding)
        } : test === 'top' ? {
            left: ((triggerBoundingBox.left + triggerBoundingBox.width / 2) - tooltipBoundingBox.width / 2),
            top: (triggerBoundingBox.top - tooltipBoundingBox.height - padding)
        } : test === 'left' ? {
            top: ((triggerBoundingBox.top + triggerBoundingBox.height / 2) - tooltipBoundingBox.height / 2),
            left: (triggerBoundingBox.left - tooltipBoundingBox.width - padding),
        } : {
            top: ((triggerBoundingBox.top + triggerBoundingBox.height / 2) - tooltipBoundingBox.height / 2),
            left: (triggerBoundingBox.right + padding),
        }));


        const capped = wouldCap(defaultPosition(placement), tooltipBoundingBox);
        if (capped.y !== 0) {
            if (capped.y === 1 && placement === 'bottom') {
                placement = 'top'
            } else if (capped.y === -1 && placement === 'top') {
                placement = 'bottom'
            }
        }
        if (capped.x !== 0) {
            if (capped.x === -1 && placement === 'left') {
                placement = 'right'
            } else if (capped.x === 1 && placement === 'right') {
                placement = 'left'
            }
        }

        const position = cap(defaultPosition(placement), tooltipBoundingBox)
        const arrowBuffer = (props.arrowBuffer || 12);
        const offsetMaxX = triggerBoundingBox.left + triggerBoundingBox.width - position.left - arrowBuffer
        const offsetMaxY = triggerBoundingBox.top + triggerBoundingBox.height - position.top - arrowBuffer
        const offsetMinX = (triggerBoundingBox.left - position.left) + arrowBuffer
        const offsetMinY = (triggerBoundingBox.top - position.top) + arrowBuffer

        const arrowPosition = {
            left: Math.max(arrowBuffer, Math.min(tooltipBoundingBox.width - arrowBuffer, Math.min(offsetMaxX, Math.max(offsetMinX, tooltipBoundingBox.width / 2)))),
            top: Math.max(arrowBuffer, Math.min(tooltipBoundingBox.height - arrowBuffer, Math.min(offsetMaxY, Math.max(offsetMinY, tooltipBoundingBox.height / 2))))
        }

        switch (placement) {
            case 'top': {
                arrowPosition.top = tooltipBoundingBox.height - 2
                break;
            }
            case 'bottom': {
                arrowPosition.top = 0
                break;
            }
            case 'left': {
                arrowPosition.left = tooltipBoundingBox.width - 2
                break;
            }
            case 'right': {
                arrowPosition.left = 0
                break;
            }
        }

        if (Settings.debug) {
            console.log('triggerBoundingBox', triggerBoundingBox)
            console.log('tooltipBoundingBox', tooltipBoundingBox)
            console.log('boundaryBox', boundaryBox)
            console.log('defaultPosition', defaultPosition(placement))
            console.log('capped', capped)
            console.log('position', position)
            console.log('offsetMaxX', offsetMaxX)
            console.log('offsetMaxY', offsetMaxY)
            console.log('offsetMinX', offsetMinX)
            console.log('offsetMinY', offsetMinY)
            console.log('arrowPosition', arrowPosition)
        }

        const hiddenPartial = triggerBoundingBox.left < boundaryBox.left || triggerBoundingBox.right > boundaryBox.right
            || triggerBoundingBox.top < boundaryBox.left || triggerBoundingBox.right > boundaryBox.right;

        const hiddenFull = (triggerBoundingBox.right < boundaryBox.left) || (triggerBoundingBox.bottom < boundaryBox.top)
            || (triggerBoundingBox.left > boundaryBox.right) || (triggerBoundingBox.top > boundaryBox.bottom)

        const hidden = hiddenFull ? 'full' : hiddenPartial ? 'partial' : 'visible';
        element.dataset['hidden'] = hidden;
        element.dataset['placement'] = placement;
        arrow.classList.remove('up', 'down', 'left', 'right');
        arrow.classList.add((placement === 'bottom' ? 'up' : placement === 'top' ? 'down' : placement === 'left' ? 'right' : 'left'))

        element.style.top = (position.top - (triggerBoundingBox.top + triggerBoundingBox.height)) + 'px';
        element.style.left = (position.left - triggerBoundingBox.left) + 'px';

        arrow.style.left = arrowPosition.left + 'px';
        arrow.style.top = arrowPosition.top + 'px';
    })

    return (
        <>
            <div {...childAttributes} style={{ position: 'relative' }}>
                <div ref={tooltipRefCallback} className="lcms-tooltip"
                >
                    {props.content}
                    <div className={"lcms-arrow"}></div>
                </div>
            </div>
        </>
    )
}
